// Currency / number / discount formatters shared across public pages.
// Locale-independent for now (en-US grouping). Wiring lots can switch to
// Intl.NumberFormat with i18n.language once locale-specific formatting matters.

export function formatAed(value: number): string {
  return `AED ${value.toLocaleString('en-US')}`;
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

export function formatDiscountPct(pct: number): string {
  const magnitude = Math.abs(pct);
  return `−${magnitude}%`;
}

/**
 * Discount magnitude for the deal-card badge: absolute value, max 2 decimals,
 * no trailing zeros, no sign and no "%" (the i18n label appends "% Below …").
 * e.g. 31.62 → "31.62", 31.6 → "31.6", 31 → "31".
 */
export function formatDiscountMagnitude(pct: number): string {
  return String(Math.round(Math.abs(pct) * 100) / 100);
}

export function formatPricePerSqft(value: number): string {
  return `${value.toLocaleString('en-US')} AED/sqft`;
}

// Admin topbar live clock — Gulf Standard Time (UTC+4, no DST, Dubai).
// Format: "DD MMM YYYY · HH:mm GST" (e.g. "11 May 2026 · 14:37 GST"). en-GB
// is the only Intl primitive available in this stack (no date-fns / dayjs);
// it formats to "11 May 2026, 14:37" — we swap the comma for the typographic
// middle-dot used in the design.
export function formatGstDateTime(date: Date = new Date()): string {
  const formatted = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Dubai',
  }).format(date);
  return `${formatted.replace(', ', ' · ')} GST`;
}

// Canonical back-office timestamp renderers (M13 BO timezone coherence).
// Every date/time visible in the back-office renders in Asia/Dubai through
// these two helpers — never a per-page Intl formatter without a timeZone
// (which silently falls back to the browser timezone) and never a hardcoded
// offset. Null/invalid ISO strings render the table em-dash.

/** "12 May 2026 · 14:37 GST" — Dubai wall-clock, explicit GST suffix. */
export function formatGstDateTimeIso(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : formatGstDateTime(date);
}

const GST_DATE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Dubai',
});

/** "12 May 2026" — date-only, still pinned to Asia/Dubai so a timestamp close
 * to midnight never shifts to the previous/next day in another timezone. */
export function formatGstDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : GST_DATE.format(date);
}

// Public-facing date renderer for DealPage footer (Spec §M4 publication date).
// Resolves to a long-form locale string — e.g. "12 May 2026" (en), "12 mai 2026"
// (fr), "12 مايو 2026" (ar). ISO strings come back from PostgREST with a Z
// suffix; `Intl.DateTimeFormat` reads them as UTC, so we render in Asia/Dubai
// to match the implicit GST authoring timezone of the editorial team.
//
// EN is force-mapped to en-GB because the default `en` (en-US) yields the
// month-first form "May 12, 2026" — we want the day-first long form across
// every locale for consistency.
export function formatPublishedDate(iso: string | null | undefined, locale: string = 'en'): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // Normalise BCP-47 region tags (`fr-FR` → `fr`) so we hit the same Intl data
  // bag i18n.language exposes app-wide.
  const primary = locale.split('-')[0]?.toLowerCase() ?? 'en';
  const resolved = primary === 'en' ? 'en-GB' : primary;
  return new Intl.DateTimeFormat(resolved, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Dubai',
  }).format(date);
}
